import { MaskedLine, FadeUp, Eyebrow } from "@/components/Reveal";
import PhotoMarquee from "@/components/PhotoMarquee";
import { Vine, FloatLeaf, Leaf } from "@/components/Botanical";

const STORY = [
  "At Salon Orgænics, we believe a great salon experience is about more than how you look when you leave — it's about how you feel while you're here.",
  "We've created a warm, welcoming and professional space where quality, care and personal attention come together. From the products we choose to the way we train our team, every detail is thoughtfully considered with one thing in mind: the long-term health and beauty of your hair.",
  "We take genuine pride in our craft and believe that great hair begins with understanding you — your style, your needs and what makes you feel your best. That's why every visit is designed around personalised, one-to-one care, never a rushed or impersonal experience.",
  "Over the years, we've built more than a clientele — we've built a community of people who trust us with their hair, return for the experience, and become part of the Salon Orgænics family.",
];

const PILLARS = [
  {
    n: "01",
    kicker: "Our Vision",
    title: "Traditional craft, a conscious future",
    body: "To redefine what it means to care for hair — bringing together the wisdom of traditional craft with the possibilities of a more conscious future. Looking forward doesn't mean leaving behind what has been learned before; we honour that heritage while continually evolving. We envision a future where beautiful hair and responsible choices go hand in hand.",
  },
  {
    n: "02",
    kicker: "Our Mission",
    title: "Curation over endless choice",
    body: "To thoughtfully curate the finest haircare products and experiences, guided by expertise, integrity and a commitment to conscious beauty. In a world where almost every product is a click away, having more choices doesn't mean making better choices. We seek out products as kind to you as they are to the world — natural and organic ingredients, non-toxic formulations, vegan and cruelty-free principles, and minimal environmental impact.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="relative min-h-[58vh] flex items-end bg-[#F9FAF8] overflow-hidden pt-36 pb-14">
        <Vine className="absolute right-6 md:right-20 top-24 h-[60%] w-auto opacity-30" flip />
        <FloatLeaf className="w-28 left-[12%] top-[22%] opacity-25" delay={0.5} />
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 w-full relative z-10">
          <MaskedLine onLoad className="mb-6">
            <Eyebrow>About the house</Eyebrow>
          </MaskedLine>
          <h1 className="font-display font-light leading-[0.95] tracking-tight text-[#0A0A0A]">
            <MaskedLine onLoad delay={0.15} className="text-[13vw] md:text-8xl">
              <span>Where hair is</span>
            </MaskedLine>
            <MaskedLine onLoad delay={0.3} className="text-[13vw] md:text-8xl">
              <span className="italic text-[#11261A]">cared for,</span>
            </MaskedLine>
            <MaskedLine onLoad delay={0.45} className="text-[13vw] md:text-8xl">
              <span className="text-[#11261A]">not just styled.</span>
            </MaskedLine>
          </h1>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24" data-testid="about-story-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <FadeUp>
              <Eyebrow>The story</Eyebrow>
              <Leaf className="w-16 h-16 mt-8 opacity-40" />
            </FadeUp>
          </div>
          <div className="lg:col-span-7 space-y-8">
            {STORY.map((p, i) => (
              <FadeUp key={i} delay={i * 0.08}>
                <p className={`leading-relaxed ${i === 0 ? "font-display font-light text-2xl md:text-3xl text-[#0A0A0A]" : "text-base md:text-lg text-[#4A4A4A]"}`}>
                  {p}
                </p>
              </FadeUp>
            ))}
            <FadeUp delay={0.4}>
              <p className="font-display italic font-light text-2xl text-[#11261A] pt-4 border-t border-[#E5E7E3]">
                Come in, slow down, and let us take care of the rest.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>
      <PhotoMarquee />

      <section className="relative bg-[#11261A] text-[#F9FAF8] grain py-20 md:py-28 overflow-hidden" data-testid="about-pillars-section">
        <FloatLeaf className="w-32 right-[6%] top-[10%] opacity-15" color="#8EA696" />
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
          <FadeUp>
            <Eyebrow dark>Vision &amp; Mission</Eyebrow>
          </FadeUp>
          <div className="mt-12 space-y-16">
            {PILLARS.map((p) => (
              <div key={p.n} className="grid md:grid-cols-12 gap-8 items-start">
                <div className="md:col-span-3">
                  <MaskedLine>
                    <span className="font-display font-light text-7xl md:text-8xl text-[#F9FAF8]/10 leading-none">{p.n}</span>
                  </MaskedLine>
                  <FadeUp delay={0.1}>
                    <p className="text-[11px] uppercase tracking-[0.35em] text-[#8EA696] mt-4">{p.kicker}</p>
                  </FadeUp>
                </div>
                <div className="md:col-span-8 md:col-start-5">
                  <MaskedLine delay={0.1}>
                    <h3 className="font-display font-light text-3xl md:text-5xl tracking-tight">{p.title}</h3>
                  </MaskedLine>
                  <FadeUp delay={0.25}>
                    <p className="mt-6 text-[#F9FAF8]/75 leading-relaxed text-base md:text-lg max-w-2xl">{p.body}</p>
                  </FadeUp>
                </div>
              </div>
            ))}
          </div>
          <FadeUp className="mt-16">
            <p className="font-display font-light text-2xl md:text-4xl leading-snug max-w-3xl border-t border-[#1B3B28] pt-12" data-testid="about-closing-belief">
              “Beauty should be thoughtful. Haircare should be conscious. And great results should
              never come at the cost of <span className="italic text-[#8EA696]">what matters.</span>”
            </p>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
